import { userSchema } from '@/model/yupSchema.mjs';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Select,
  Button,
  Switch
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  callToCreateUser,
  setCreateUserEmailError,
  setIsUserCreated
} from '../ducks/slice';
import ModalComponent from '@/common/Modal';
import { CreateUserInterface } from '../types';
import {
  getCreateUserEmailError,
  getIsCreatingUser,
  getIsUserCreated
} from '../ducks/selectors';
import { getIsImpersonating, getStoreData } from '@/redux/app-selectors';
import cookie from 'js-cookie';
import AppConfig from '@/utils/app-config.mjs';

export default function AddUserModal({
  isOpen,
  onClose,
  reFetchData
}: {
  isOpen: boolean;
  onClose: () => void;
  reFetchData(): void;
}) {
  const dispatch = useDispatch();
  const storeData = getStoreData();
  const [isEnabledMFA, setIsEnabledMFA] = useState<boolean | undefined>(false);
  
  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(userSchema),
    mode: 'onChange',
    values: {
      firstName: '',
      lastName: '',
      groupName: '',
      email: '',
      phone: '',
      isMFAEnabled: false
    }
  });
  const isCreatingUser = getIsCreatingUser();
  const isUserCreated = getIsUserCreated();
  
  const isImpersonating = getIsImpersonating();

  const isGlobalAdminImpersonating = () => {
      return cookie.get(AppConfig.cookieStoreIdName) && isImpersonating;
  };

  const submitHandler: SubmitHandler<CreateUserInterface> = (data) => {
    data.phone = `+1${data.phone}`;
    if(isEnabledMFA){
      data.isMFAEnabled = isEnabledMFA;
    }
    dispatch(callToCreateUser(data));
  };

  useEffect(() => {
    if (storeData) {
      setIsEnabledMFA(storeData.is_mfa_enabled);
      setValue("isMFAEnabled", storeData.is_mfa_enabled || false);
    }
  }, [storeData]);

  useEffect(() => {
    if (isUserCreated) {
      dispatch(setIsUserCreated(false));
      onClose();
      reFetchData();
    }
  }, [isUserCreated]);

  useEffect(() => {
    return () => {
      dispatch(setCreateUserEmailError(''));
    }
  }, [])
  const createUserEmailError = getCreateUserEmailError();
  const onChangeEmailInput = () => {
    dispatch(setCreateUserEmailError(''));
  };
  const onChangeMFAEnabled = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsEnabledMFA(event.target.checked);
  };

  return (
    <ModalComponent
      title="Add a New User"
      isOpen={isOpen}
      onClose={onClose}
      variant={2}
      modalType="addUser"
      footer={
        <>
          <Button
            colorScheme="blue"
            background="#ffffff"
            color="#0C1E46"
            border="1px solid #0C1E46"
            padding="8px 11px"
            marginRight="20px"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            background="#0C1E46"
            padding="8px 20px"
            onClick={handleSubmit(submitHandler)}
            disabled={isCreatingUser}
            isLoading={isCreatingUser}
          >
            Save
          </Button>
        </>
      }
    >
      <Box className="user-modal-wrapper">
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">First Name*:</FormLabel>
          <Input
            className="user-from-input"
            type="text"
            placeholder="Enter First Name"
            {...register('firstName')}
          />
          {errors && errors.firstName ? (
            <div className="form-error">{errors.firstName.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Last Name*:</FormLabel>
          <Input
            className="user-from-input"
            type="text"
            placeholder="Enter Last Name"
            {...register('lastName')}
          />
          {errors && errors.lastName ? (
            <div className="form-error">{errors.lastName.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Email*:</FormLabel>
          <Input
            className="user-from-input"
            type="email"
            placeholder="Enter Email"
            {...register('email', {
              onChange: onChangeEmailInput
            })}
          />
          {errors && errors.email ? (
            <div className="form-error">{errors.email.message}</div>
          ) : (
            ''
          )}
          {createUserEmailError ? (
            <div className="form-error">{createUserEmailError}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Role*:</FormLabel>
          <Select
            className="user-from-input"
            placeholder="-Select Role-"
            {...register('groupName')}
          >
            <option value="AGENT">Store Agent</option>
            <option value="STORE_ADMIN">Store Admin</option>
          </Select>
          {errors && errors.groupName ? (
            <div className="form-error">{errors.groupName.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Phone*:</FormLabel>
          <Input
            className="user-from-input"
            type="text"
            maxLength={10}
            placeholder="Enter Phone Number"
            {...register('phone')}
          />
          {errors && errors.phone ? (
            <div className="form-error">{errors.phone.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        {/* {isGlobalAdminImpersonating()?
        <FormControl style={{ width: '300px' }}>
        <FormLabel className="user-form-label">Is MFA Enabled*:</FormLabel>
        <Controller
          name="isMFAEnabled"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Switch
              size="md"
              isChecked={value}
              onChange={(e) => {
                onChange(e); // This updates the form state in react-hook-form
                onChangeMFAEnabled(e); 
              }}
            />
          )}
        />
        {errors && errors.isMFAEnabled ? (
          <div className="form-error">{errors.isMFAEnabled.message}</div>
        ) : (
          ''
        )}
      </FormControl>:''} */}
      </Box>
    </ModalComponent>
  );
}
